import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/toast";
import {
  RECOVERY_POSTURE,
  RECOVERY_HSM_SHARDS,
  RECOVERY_HUMAN_SHARDS,
  RECOVERY_CUSTODIANS,
  RECOVERY_HISTORY,
  RECOVERY_TEST_TOAST,
} from "@/data/mock";
import { usePermission } from "@/hooks/use-permission";

/* RCV-00 — Recovery Posture Overview.
   Canonical source: prototype/pages/recovery/index.html (no current
   Figma frame located for any RCV screen). Reproduced verbatim except:
   the canonical action bar's "Active Ceremony: REC-2024-001 →" link is
   OMITTED here — it contradicts this same page's own "Active Requests:
   0 / No pending requests" KPI and targets hsm-auth.html, a prototype
   page outside the 6 required RCV-00..05 screens. Documented in
   analysis/RECOVERY_IMPLEMENTATION_REPORT.md, not silently dropped. */

const RESULT_TONE: Record<string, "success" | "warning"> = {
  Passed: "success",
  Partial: "warning",
};

export function RecoveryOverview() {
  const canRun = usePermission("admin:access");
  function runTest() {
    toast({ variant: "info", title: RECOVERY_TEST_TOAST });
  }

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-xl font-extrabold text-ink">Recovery / Key Continuity</h1>
        <p className="text-sm text-muted">Posture, custodians, and history for the organization&apos;s key recovery ceremony.</p>
      </div>

      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <KpiCard label="Key Shards" value={RECOVERY_POSTURE.keyShards} sub={RECOVERY_POSTURE.shardScheme} />
        <KpiCard label="Custodians" value={RECOVERY_POSTURE.custodianCount} sub={RECOVERY_POSTURE.custodiansSub} />
        <KpiCard label="Active Requests" value={RECOVERY_POSTURE.activeRequests} sub={RECOVERY_POSTURE.activeRequestsSub} />
        <KpiCard label="Last Tested" value={RECOVERY_POSTURE.lastTested} sub={RECOVERY_POSTURE.lastTestedSub} />
      </div>

      <div className="mb-5 grid gap-4 lg:grid-cols-2">
        <div className="rounded-[10px] border border-line bg-surface p-6">
          <div className="mb-4 flex items-center justify-between">
            <div className="text-base font-bold text-ink">Recovery Posture</div>
            <Badge variant="success" size="sm">{RECOVERY_POSTURE.status}</Badge>
          </div>
          <dl className="space-y-2.5 text-sm">
            <Row k="Scheme" v={RECOVERY_POSTURE.scheme} />
            <Row k="Key Algorithm" v={RECOVERY_POSTURE.keyAlgorithm} />
            <Row k="Shard Storage" v={RECOVERY_POSTURE.shardStorage} />
            <Row k="Next Required Test" v={RECOVERY_POSTURE.nextTest} />
            <Row k="Compliance Requirement" v={RECOVERY_POSTURE.complianceRequirement} />
          </dl>
          <Button variant="secondary" size="sm" className="mt-4" disabled={!canRun} onClick={() => canRun && runTest()}>↺ Test Recovery</Button>
        </div>

        <div className="rounded-[10px] border border-line bg-surface p-6">
          <div className="mb-4 text-base font-bold text-ink">Shard Distribution</div>
          <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted">HSM Shards (3/5)</div>
          <ul className="mb-4 divide-y divide-line">
            {RECOVERY_HSM_SHARDS.map((s) => (
              <li key={s.label} className="flex items-center justify-between py-2 text-sm">
                <span className="text-ink">{s.label}</span>
                <span className="text-muted">{s.holder}</span>
              </li>
            ))}
          </ul>
          <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted">Human Shards (2/5)</div>
          <ul className="divide-y divide-line">
            {RECOVERY_HUMAN_SHARDS.map((s) => (
              <li key={s.label} className="flex items-center justify-between py-2 text-sm">
                <span className="text-ink">{s.label}</span>
                <span className="text-muted">{s.holder}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mb-5 overflow-hidden rounded-[10px] border border-line bg-surface">
        <div className="border-b border-line px-6 py-4 text-base font-bold text-ink">Custodians</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left text-xs font-semibold uppercase tracking-wide text-muted">
                <th className="px-6 py-2.5">Name</th>
                <th className="px-3 py-2.5">Role</th>
                <th className="px-3 py-2.5">Type</th>
                <th className="px-3 py-2.5">Status</th>
                <th className="px-6 py-2.5">Last Verified</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {RECOVERY_CUSTODIANS.map((c) => (
                <tr key={c.name}>
                  <td className="px-6 py-2.5 font-medium text-ink">{c.name}</td>
                  <td className="px-3 py-2.5 text-muted">{c.role}</td>
                  <td className="px-3 py-2.5 text-muted">{c.type}</td>
                  <td className="px-3 py-2.5">
                    <Badge variant={c.statusTone} size="sm">{c.status}</Badge>
                  </td>
                  <td className="px-6 py-2.5 text-muted">{c.lastVerified}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mb-5 flex flex-wrap gap-2.5">
        <Link to="/recovery/request">
          <Button>↺ Initiate Recovery Request</Button>
        </Link>
      </div>

      <div className="overflow-hidden rounded-[10px] border border-line bg-surface">
        <div className="border-b border-line px-6 py-4 text-base font-bold text-ink">Recovery History</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left text-xs font-semibold uppercase tracking-wide text-muted">
                <th className="px-6 py-2.5">Date</th>
                <th className="px-3 py-2.5">Type</th>
                <th className="px-3 py-2.5">Participants</th>
                <th className="px-3 py-2.5">Duration</th>
                <th className="px-3 py-2.5">Result</th>
                <th className="px-6 py-2.5">Ref</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {RECOVERY_HISTORY.map((h) => (
                <tr key={h.ref}>
                  <td className="px-6 py-2.5 text-ink">{h.date}</td>
                  <td className="px-3 py-2.5 text-muted">{h.type}</td>
                  <td className="px-3 py-2.5 text-muted">{h.participants}</td>
                  <td className="px-3 py-2.5 text-muted">{h.duration}</td>
                  <td className="px-3 py-2.5">
                    <Badge variant={RESULT_TONE[h.result]} size="sm">{h.result}</Badge>
                  </td>
                  <td className="px-6 py-2.5 font-mono text-xs text-muted">{h.ref}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="rounded-lg border border-line bg-surface px-4 py-3.5">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted">{label}</div>
      <div className="mt-1 text-2xl font-extrabold text-ink">{value}</div>
      <div className="mt-0.5 text-xs text-muted">{sub}</div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-muted">{k}</dt>
      <dd className="text-right font-medium text-ink">{v}</dd>
    </div>
  );
}
